// src/hooks/useStreamingMessage.ts
import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { ChatMessageEvent } from 'events/types';
import {
  registerChatMessageCallback,
  unregisterChatMessageCallback,
} from 'events/webSocketHandlers';
import { normalizeBackendUtcIsoString } from 'utils/dateUtils';
import type { ChatMsg } from 'types';

/** Активный task_id для свободного чата — не должен совпадать с ключом сценария, иначе ответ дублируется в оба чата. */
export const FREE_CHAT_TASK_ID_KEY = 'free_chat_task_id';
/** Активный task_id для чата внутри сценария */
export const SCENARIO_CHAT_TASK_ID_KEY = 'scenario_chat_task_id';

const streamChatKey = (taskId: string) => `stream_chat_${taskId}`;

// ─── Темп набора текста ────────────────────────────────────────────────────
// Скорость фиксируется на каждую пришедшую «пачку» текста и держится постоянной
// до следующей — внутри пачки печать строго равномерная, без затухания
// «сначала быстро, потом медленно». Скорость подбирается так, чтобы допечатать
// текущий хвост примерно за TYPING_DRAIN_SEC, но в пределах [MIN, MAX].
const TYPING_MIN_RATE = 80; // симв/с — нижняя граница (мелкие догоны, хвост)
const TYPING_MAX_RATE = 260; // симв/с — верхняя граница (крупная пачка не «вываливается»)
const TYPING_DRAIN_SEC = 1; // за сколько секунд стремимся допечатать текущий хвост

export interface UseStreamingMessageResult {
  streamingMessage: ChatMsg | null;
  displayedContent: string;
  currentTaskId: string | null;
  progressLabel: string | null;
  resetStreaming: () => void;
}

export const useStreamingMessage = (
  setChat: React.Dispatch<React.SetStateAction<ChatMsg[]>>,
  setLoading: (loading: boolean) => void,
  taskIdStorageKey: string,
  // Пока true — посимвольный набор «заморожен» (например, идёт обфускация данных
  // перед внешней моделью): контент уже мог прийти, но печать стартует только
  // после снятия паузы, чтобы ответ не «вываливался» целиком.
  paused = false
): UseStreamingMessageResult => {
  const [streamingMessage, setStreamingMessage] = useState<ChatMsg | null>(null);
  const [targetContent, setTargetContent] = useState('');
  const [displayedContent, setDisplayedContent] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [finalTimestamp, setFinalTimestamp] = useState<string | null>(null);
  const [progressLabel, setProgressLabel] = useState<string | null>(null);

  const animationFrameRef = useRef<number | null>(null);
  const lastUpdateTimeRef = useRef<number>(0);
  // Track actual displayed length in a ref to avoid stale closures in rAF
  const displayedLengthRef = useRef<number>(0);
  // Постоянная скорость печати (симв/с) текущей пачки — пересчитывается только
  // при приходе нового контента, а не каждый кадр (иначе скорость «затухает»).
  const typingRateRef = useRef<number>(TYPING_MIN_RATE);
  const localStorageTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [currentTaskId, setCurrentTaskId] = useState<string | null>(() =>
    localStorage.getItem(taskIdStorageKey)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTaskId(localStorage.getItem(taskIdStorageKey));
    }, 100);
    return () => clearInterval(interval);
  }, [taskIdStorageKey]);

  // Keep ref in sync with state for rAF closure
  useEffect(() => {
    displayedLengthRef.current = displayedContent.length;
  }, [displayedContent]);

  // Пересчитываем постоянную скорость печати при КАЖДОЙ новой пачке контента
  // (но не каждый кадр). Скорость = «сколько печатать в секунду, чтобы добрать
  // текущий хвост за TYPING_DRAIN_SEC», ограниченная сверху/снизу. Внутри пачки
  // скорость не меняется → равномерный поток без эффекта «быстро→медленно».
  useEffect(() => {
    const backlog = targetContent.length - displayedLengthRef.current;
    if (backlog <= 0) return;
    typingRateRef.current = Math.min(
      TYPING_MAX_RATE,
      Math.max(TYPING_MIN_RATE, backlog / TYPING_DRAIN_SEC)
    );
  }, [targetContent]);

  useEffect(() => {
    if (!targetContent) {
      setDisplayedContent('');
      displayedLengthRef.current = 0;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    // Пауза (обфускация): не запускаем печать, держим текущую позицию.
    if (paused) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    if (displayedContent.length < targetContent.length) {
      // Равномерный темп: печатаем с ПОСТОЯННОЙ скоростью typingRateRef (симв/с),
      // зафиксированной при приходе этой пачки. Шаг считаем от реально прошедшего
      // времени (а не от кадра) — скорость не зависит от частоты кадров. Скорость
      // не пересчитывается покадрово, поэтому нет затухания «быстро→медленно»:
      // внутри пачки поток ровный, а адаптация к объёму происходит только на
      // границах пачек (новый контент по WS/polling).
      const INTERVAL_MS = 16; // ~один кадр; задаёт гранулярность тика

      const animate = (currentTime: number) => {
        // Ограничиваем дельту: на первом кадре ref ещё хранит старое время, а при
        // throttle свёрнутой вкладки rAF может «склеить» кадры — без clamp это
        // выльется в один огромный скачок текста вместо равномерной печати.
        const elapsed = Math.min(currentTime - lastUpdateTimeRef.current, 100);
        if (elapsed >= INTERVAL_MS) {
          lastUpdateTimeRef.current = currentTime;
          setDisplayedContent((prev) => {
            const step = Math.max(1, Math.round((typingRateRef.current * elapsed) / 1000));
            const nextLength = Math.min(prev.length + step, targetContent.length);
            const next = targetContent.slice(0, nextLength);
            displayedLengthRef.current = next.length;
            return next;
          });
        }
        // Use ref (not stale closure) to decide whether to continue
        if (displayedLengthRef.current < targetContent.length) {
          animationFrameRef.current = requestAnimationFrame(animate);
        }
      };

      animationFrameRef.current = requestAnimationFrame(animate);

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      };
    } else if (
      isComplete &&
      displayedContent.length === targetContent.length &&
      targetContent &&
      finalTimestamp !== null
    ) {
      // Animation complete + success received → add to chat
      setChat((prev) => [
        ...prev,
        {
          role: 'AI' as const,
          content: targetContent,
          created_at: finalTimestamp,
        },
      ]);
      setStreamingMessage(null);
      setTargetContent('');
      setDisplayedContent('');
      displayedLengthRef.current = 0;
      localStorage.removeItem(taskIdStorageKey);
      setIsComplete(false);
      setFinalTimestamp(null);
      setLoading(false);
    }
  }, [
    targetContent,
    displayedContent,
    isComplete,
    finalTimestamp,
    paused,
    setChat,
    setLoading,
    taskIdStorageKey,
  ]);

  const saveToLocalStorage = useCallback((task_id: string, content: string) => {
    if (localStorageTimerRef.current) clearTimeout(localStorageTimerRef.current);
    localStorageTimerRef.current = setTimeout(() => {
      localStorage.setItem(
        streamChatKey(task_id),
        JSON.stringify({ role: 'AI', content, isStreaming: true })
      );
    }, 100);
  }, []);

  const handleChatMessage = useCallback(
    (data: ChatMessageEvent) => {
      const { task_id, status, content, created_at, progress } = data;
      if (task_id !== currentTaskId) return;

      if (status === 'stream') {
        setTargetContent(content ?? '');
        setProgressLabel(progress?.label ?? null);
        setStreamingMessage({
          role: 'AI',
          content: content ?? '',
          created_at: new Date().toISOString(),
        });
        saveToLocalStorage(task_id, content ?? '');
      } else if (status === 'success') {
        setTargetContent(content ?? '');
        setFinalTimestamp(normalizeBackendUtcIsoString(created_at ?? new Date().toISOString()));
        setIsComplete(true);
        setProgressLabel(null);
        if (localStorageTimerRef.current) clearTimeout(localStorageTimerRef.current);
        localStorage.removeItem(streamChatKey(task_id));
        setStreamingMessage({
          role: 'AI',
          content: content ?? '',
          created_at: new Date().toISOString(),
        });
      } else if (status === 'error') {
        setLoading(false);
        setStreamingMessage(null);
        setTargetContent('');
        setDisplayedContent('');
        setProgressLabel(null);
        localStorage.removeItem(taskIdStorageKey);
        localStorage.removeItem(streamChatKey(task_id));
      }
    },
    [currentTaskId, saveToLocalStorage, setLoading, taskIdStorageKey]
  );

  useEffect(() => {
    registerChatMessageCallback(handleChatMessage);
    return () => {
      unregisterChatMessageCallback(handleChatMessage);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (localStorageTimerRef.current) clearTimeout(localStorageTimerRef.current);
    };
  }, [handleChatMessage]);

  const resetStreaming = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (localStorageTimerRef.current) {
      clearTimeout(localStorageTimerRef.current);
      localStorageTimerRef.current = null;
    }
    localStorage.removeItem(taskIdStorageKey);
    if (currentTaskId) localStorage.removeItem(streamChatKey(currentTaskId));
    setStreamingMessage(null);
    setTargetContent('');
    setDisplayedContent('');
    displayedLengthRef.current = 0;
    setIsComplete(false);
    setFinalTimestamp(null);
    setProgressLabel(null);
  }, [currentTaskId, taskIdStorageKey]);

  return {
    streamingMessage,
    displayedContent,
    currentTaskId,
    progressLabel,
    resetStreaming,
  };
};
